import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Edit, Maximize2 } from 'lucide-react';
import { 
  intervalToDays, 
  calculateLaycanStatus
} from '../../lib/pcerUtils';

const FIELD_DEFINITIONS = [
  { id: 'vessel_name', label: 'Vessel', type: 'category' },
  { id: 'voyage_number', label: 'Voyage Number', type: 'category' },
  { id: 'port_name', label: 'Port/Position', type: 'category' },
  { id: 'ata', label: 'ATA', type: 'date' },
  { id: 'ats', label: 'ATS', type: 'date' },
  { id: 'actual_port_days', label: 'Actual Port Days', type: 'numeric' },
  { id: 'planned_days', label: 'Planned Port Days', type: 'numeric' },
  { id: 'laycan_deviation_days', label: 'Laycan Deviation Days', type: 'numeric' },
  { id: 'port_stay_deviation', label: 'Port Stay Deviation Days', type: 'numeric' },
  { id: 'laycan_status', label: 'Laycan Status', type: 'category' }
];

const AGGREGATIONS = [
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
  { id: 'count', label: 'Count' },
  { id: 'distinctCount', label: 'Distinct Count' },
  { id: 'min', label: 'Min' },
  { id: 'max', label: 'Max' },
  { id: 'median', label: 'Median' }
];

const NUMBER_FORMATS = [
  { id: 'number', label: 'Number', format: (v) => v?.toFixed(2) },
  { id: 'currency', label: 'Currency', format: (v) => `$${v?.toFixed(2)}` },
  { id: 'percent', label: 'Percent', format: (v) => `${(v * 100)?.toFixed(2)}%` },
  { id: 'integer', label: 'Integer', format: (v) => Math.round(v) }
];

// Widget version for dashboard
export function PivotTableWidget({ widget, reportData, onRemove, onEdit, onMaximize }) {
  const config = widget.config || {};
  
  // Process data with derived fields
  const processedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    
    return reportData.map(row => ({
      ...row,
      port_stay_deviation: row.actual_port_days && row.planned_days 
        ? parseFloat(row.actual_port_days) - parseFloat(row.planned_days) 
        : null,
      laycan_status: calculateLaycanStatus(row.ata, row.laycan_start_date, row.laycan_end_date),
      planned_days: row.planned_days !== null ? parseFloat(row.planned_days) : 
                    (row.working_days && row.idle_days ? 
                      intervalToDays(row.working_days) + intervalToDays(row.idle_days) : null)
    }));
  }, [reportData]);

  // Group by date helper
  const groupByDate = (dateStr, grouping) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    
    switch (grouping) {
      case 'day':
        return date.toISOString().split('T')[0];
      case 'week':
        const week = Math.ceil((date - new Date(date.getFullYear(), 0, 1)) / 604800000);
        return `${date.getFullYear()}-W${week}`;
      case 'month':
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      case 'quarter':
        const quarter = Math.ceil((date.getMonth() + 1) / 3);
        return `${date.getFullYear()}-Q${quarter}`;
      case 'year':
        return String(date.getFullYear());
      default:
        return dateStr;
    }
  };

  // Aggregate values
  const aggregate = (values, aggregation) => {
    if (values.length === 0) return 0;
    
    switch (aggregation) {
      case 'sum':
        return values.reduce((sum, v) => sum + v, 0);
      case 'avg':
        return values.reduce((sum, v) => sum + v, 0) / values.length;
      case 'count':
        return values.length;
      case 'distinctCount':
        return new Set(values).size;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
      case 'median':
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
      default:
        return values.reduce((sum, v) => sum + v, 0);
    }
  };

  // Build pivot table data
  const pivotData = useMemo(() => {
    if (processedData.length === 0 || !config.rows || config.rows.length === 0) {
      return { rows: [], columns: [], data: {} };
    }

    const grouped = {};
    
    processedData.forEach(row => {
      // Build row key
      const rowKey = config.rows.map(field => {
        const fieldDef = FIELD_DEFINITIONS.find(f => f.id === field);
        if (fieldDef?.type === 'date' && config.dateGrouping?.[field]) {
          return groupByDate(row[field], config.dateGrouping[field]);
        }
        return row[field] || 'N/A';
      }).join('|||');
      
      // Build column key
      const colKey = (config.columns || []).map(field => {
        const fieldDef = FIELD_DEFINITIONS.find(f => f.id === field);
        if (fieldDef?.type === 'date' && config.dateGrouping?.[field]) {
          return groupByDate(row[field], config.dateGrouping[field]);
        }
        return row[field] || 'N/A';
      }).join('|||');
      
      const key = `${rowKey}::${colKey}`;
      
      if (!grouped[key]) {
        grouped[key] = { rowKey, colKey, values: {} };
        (config.values || []).forEach(v => {
          grouped[key].values[v.field] = [];
        });
      }
      
      (config.values || []).forEach(v => {
        const value = v.field === '_count' ? 1 : parseFloat(row[v.field]);
        if (!isNaN(value)) {
          grouped[key].values[v.field].push(value);
        }
      });
    });

    // Aggregate
    const aggregated = {};
    Object.entries(grouped).forEach(([key, data]) => {
      aggregated[key] = {};
      (config.values || []).forEach(v => {
        aggregated[key][v.field] = aggregate(data.values[v.field], v.aggregation);
      });
    });

    // Extract unique rows and columns
    const rowKeys = [...new Set(Object.values(grouped).map(d => d.rowKey))].sort();
    const colKeys = [...new Set(Object.values(grouped).map(d => d.colKey))].sort();

    return { rows: rowKeys, columns: colKeys, data: aggregated };
  }, [processedData, config]);

  // Format number
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    const formatter = NUMBER_FORMATS.find(f => f.id === (config.numberFormat || 'number'));
    return formatter ? formatter.format(value) : value.toFixed(2);
  };

  // Get conditional formatting style
  const getConditionalStyle = (value, allValues) => {
    if (!config.conditionalFormat || config.conditionalFormat === 'none' || !value) return {};
    
    const values = allValues.filter(v => v !== null && v !== undefined);
    if (values.length === 0) return {};
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const normalized = range > 0 ? (value - min) / range : 0;
    
    if (config.conditionalFormat === 'heatmap') {
      const intensity = Math.round(normalized * 255);
      return {
        backgroundColor: `rgb(${255 - intensity}, ${255 - intensity}, 255)`,
        fontWeight: normalized > 0.7 ? 'bold' : 'normal'
      };
    } else if (config.conditionalFormat === 'bars') {
      return {
        background: `linear-gradient(to right, #3b82f6 ${normalized * 100}%, transparent ${normalized * 100}%)`,
        fontWeight: normalized > 0.7 ? 'bold' : 'normal'
      };
    }
    
    return {};
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 cursor-move">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{widget.title}</CardTitle>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            {onMaximize && (
              <Button variant="ghost" size="sm" onClick={onMaximize} title="Maximize">
                <Maximize2 className="w-4 h-4" />
              </Button>
            )}
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {onRemove && (
              <Button variant="ghost" size="sm" onClick={onRemove} title="Remove">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-auto">
        {pivotData.rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            No data or configuration incomplete
          </div>
        ) : (
          <div className="overflow-auto h-full">
            <table className="border-collapse text-xs w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {config.rows?.map((field, idx) => (
                    <th key={idx} className="border px-2 py-1 text-left font-semibold text-xs">
                      {FIELD_DEFINITIONS.find(f => f.id === field)?.label}
                    </th>
                  ))}
                  {pivotData.columns.length === 0 ? (
                    (config.values || []).map((value, idx) => (
                      <th key={idx} className="border px-2 py-1 text-right font-semibold text-xs">
                        {AGGREGATIONS.find(a => a.id === value.aggregation)?.label}
                      </th>
                    ))
                  ) : (
                    pivotData.columns.map(col => (
                      <th key={col} className="border px-2 py-1 text-right font-semibold text-xs">
                        {col}
                      </th>
                    ))
                  )}
                  {config.showTotals && pivotData.columns.length > 0 && (
                    <th className="border px-2 py-1 text-right font-semibold text-xs bg-blue-50">
                      Total
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pivotData.rows.map((row, rowIdx) => {
                  const rowParts = row.split('|||');
                  const rowTotal = pivotData.columns.reduce((sum, col) => {
                    const key = `${row}::${col}`;
                    const value = pivotData.data[key]?.[config.values?.[0]?.field] || 0;
                    return sum + value;
                  }, 0);

                  const allValues = pivotData.columns.map(col => {
                    const key = `${row}::${col}`;
                    return pivotData.data[key]?.[config.values?.[0]?.field] || 0;
                  });

                  return (
                    <tr key={rowIdx} className="hover:bg-gray-50">
                      {rowParts.map((part, idx) => (
                        <td key={idx} className="border px-2 py-1 text-xs">
                          {part}
                        </td>
                      ))}
                      {pivotData.columns.length === 0 ? (
                        (config.values || []).map((value, idx) => {
                          const key = `${row}::`;
                          const val = pivotData.data[key]?.[value.field] || 0;
                          return (
                            <td
                              key={idx}
                              className="border px-2 py-1 text-right text-xs"
                              style={getConditionalStyle(val, allValues)}
                            >
                              {formatNumber(val)}
                            </td>
                          );
                        })
                      ) : (
                        pivotData.columns.map((col, colIdx) => {
                          const key = `${row}::${col}`;
                          const val = pivotData.data[key]?.[config.values?.[0]?.field] || 0;
                          return (
                            <td
                              key={colIdx}
                              className="border px-2 py-1 text-right text-xs"
                              style={getConditionalStyle(val, allValues)}
                            >
                              {formatNumber(val)}
                            </td>
                          );
                        })
                      )}
                      {config.showTotals && pivotData.columns.length > 0 && (
                        <td className="border px-2 py-1 text-right font-semibold text-xs bg-blue-50">
                          {formatNumber(rowTotal)}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {config.showTotals && pivotData.rows.length > 0 && (
                  <tr className="bg-blue-100 font-semibold">
                    <td className="border px-2 py-1 text-xs" colSpan={config.rows?.length || 1}>
                      Total
                    </td>
                    {pivotData.columns.length === 0 ? (
                      (config.values || []).map((value, idx) => {
                        const total = pivotData.rows.reduce((sum, row) => {
                          const key = `${row}::`;
                          return sum + (pivotData.data[key]?.[value.field] || 0);
                        }, 0);
                        return (
                          <td key={idx} className="border px-2 py-1 text-right text-xs">
                            {formatNumber(total)}
                          </td>
                        );
                      })
                    ) : (
                      <>
                        {pivotData.columns.map((col, idx) => {
                          const total = pivotData.rows.reduce((sum, row) => {
                            const key = `${row}::${col}`;
                            return sum + (pivotData.data[key]?.[config.values?.[0]?.field] || 0);
                          }, 0);
                          return (
                            <td key={idx} className="border px-2 py-1 text-right text-xs">
                              {formatNumber(total)}
                            </td>
                          );
                        })}
                        {pivotData.columns.length > 0 && (
                          <td className="border px-2 py-1 text-right text-xs bg-blue-200">
                            {formatNumber(
                              pivotData.columns.reduce((sum, col) => {
                                return sum + pivotData.rows.reduce((rowSum, row) => {
                                  const key = `${row}::${col}`;
                                  return rowSum + (pivotData.data[key]?.[config.values?.[0]?.field] || 0);
                                }, 0);
                              }, 0)
                            )}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PivotTableWidget;
